import React from "react";
import {
  makeStyles,
  Theme,
  createStyles,
  Breadcrumbs,
  Typography,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { OtherTreeNodeName, OtherTreeNodeLabel } from "./model/TreeItemModel";

/** スタイル定義 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    root: {
      //
    },
    selected: {
      padding: "0.2rem 0.5rem",
      backgroundColor: "rgba(0, 0, 0, 0.1)",
      fontSize: "0.9rem",
    },
    normal: {
      padding: "0.2rem 0.5rem",
      fontSize: "0.9rem",
    },
  })
);

interface CrumbsProps {
  index: number;
  node: BreadNode;
  selected: boolean;
  onClick: (path: string) => void;
}

type BreadNode = {
  title: string;
  path: string;
};

function Content(props: CrumbsProps) {
  const classes = useStyles();

  const { index, node, selected, onClick } = props;

  const handleOnClick = () => {
    onClick(node.path);
  };

  if (index === 0) {
    // 第一階層ノード（カテゴリ）
    return (
      <>
        <Typography
          component="span"
          color="textPrimary"
          className={classes.normal}
        >
          {node.title}
        </Typography>
      </>
    );
  }
  // 第二階層以下ノード（仮想フォルダ）
  return (
    <Typography
      color="textPrimary"
      style={{ cursor: "pointer" }}
      onClick={handleOnClick}
      className={selected ? classes.selected : classes.normal}
    >
      {node.title}
    </Typography>
  );
}

interface Props {
  path: string;
  configId: number;
  excludes: string[];
  onClick: (path: string, configId: number, excludes: string[]) => void;
  onError?: (error: any) => void;
}

/** パンくずリスト */
export default function VirtualFolderBreadcrumbs(props: Props) {
  const classes = useStyles();

  const prevPath = React.useRef<string>("");
  const selectedPath = React.useRef<string>("");
  const { path, configId, excludes, onClick, onError } = props;

  /** パンくず階層の配列 */
  const nodes: BreadNode[] = path
    .split("/")
    .map((_p, idx, ary) => {
      // 先頭から現在の要素までを切り出す
      const pathOfPart = ary.slice(0, idx + 1);
      // 先頭から現在の要素までのパスを作る([分類1, 分類1/分類2, 分類1/分類2/分類3])
      return pathOfPart.reduce(
        (preVal, curVal) => (preVal ? `${preVal}/${curVal}` : curVal),
        ""
      );
    })
    .map(n => {
      let title = n.split("/").pop() || "";
      // その他の場合
      if (title === OtherTreeNodeName) {
        title = OtherTreeNodeLabel;
      }
      const bn: BreadNode = {
        path: n,
        title,
      };
      return bn;
    });

  // 同じパスなら選択パスも同じ、違うならクリア
  selectedPath.current = prevPath.current === path ? selectedPath.current : "";
  prevPath.current = path;

  /** パンくずクリックハンドラ */
  const handleOnClick = (newSelectedPath: string) => {
    selectedPath.current = newSelectedPath;

    const otherIndex = newSelectedPath.indexOf(OtherTreeNodeName);
    if (otherIndex >= 0) {
      // その他の場合は末尾パス(/その他)を削除
      const excludesPath = newSelectedPath.slice(0, otherIndex - 1);
      onClick(excludesPath, configId, excludes);
    } else {
      onClick(newSelectedPath, configId, []);
    }
  };

  //--------------------
  // コンポーネント
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-end",
      }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        className={classes.root}
      >
        {nodes.map((node, index) => (
          <Content
            key={node.path}
            index={index}
            node={node}
            selected={node.path === selectedPath.current}
            onClick={handleOnClick}
          />
        ))}
      </Breadcrumbs>
    </div>
  );
}
